import React, { useState, useEffect, useMemo, useRef } from "react";
import PriceSparkline from "./PriceSparkline";
import TrackButton from "../common/TrackButton";

const HORSE_ICONS = [
  "/images/horse-5.webp",
  "/images/horse-6.webp",
  "/images/horse-7.webp",
];

/* ---------------------------------------------------------------
   Colour helpers — reuse the existing palette from other tools
   --------------------------------------------------------------- */

// Strike rate colour scale (standard 7-colour palette from TrainerHeatmap.jsx)
const INSUF = { bg: "#d5d0ca", text: "var(--pencil)", shadow: "none" };
const WHITE = { text: "#fff", shadow: "0 1px 2px rgba(0,0,0,0.3)" };

function srColour(sr, signal) {
  if (!signal || signal === "insufficient" || sr === null || sr === undefined)
    return INSUF;
  if (sr >= 25) return { bg: "#5c8a5e", ...WHITE };
  if (sr >= 18) return { bg: "#7a9e6d", ...WHITE };
  if (sr >= 12) return { bg: "#a3b88c", ...WHITE };
  if (sr >= 8)  return { bg: "#cdb87a", ...WHITE };
  if (sr >= 5)  return { bg: "#c99b6d", ...WHITE };
  if (sr >= 2)  return { bg: "#c07e58", ...WHITE };
  return { bg: "#b85c3c", ...WHITE };
}

// Going bias colour scale (standard 7-colour palette from BiasHeatmap.jsx)
function biasColour(score) {
  if (score === null || score === undefined) return INSUF;
  if (score >= 60)  return { bg: "#5c8a5e", ...WHITE };
  if (score >= 40)  return { bg: "#7a9e6d", ...WHITE };
  if (score >= 20)  return { bg: "#a3b88c", ...WHITE };
  if (score >= 0)   return { bg: "#cdb87a", ...WHITE };
  if (score >= -20) return { bg: "#c99b6d", ...WHITE };
  if (score >= -40) return { bg: "#c07e58", ...WHITE };
  return { bg: "#b85c3c", ...WHITE };
}

/* ---------------------------------------------------------------
   Weight formatter — lbs to stones-pounds (UK racing convention)
   --------------------------------------------------------------- */

function formatWeight(lbs) {
  if (lbs == null) return null;
  const stones = Math.floor(lbs / 14);
  const pounds = lbs % 14;
  return `${stones}-${pounds}`;
}

/* ---------------------------------------------------------------
   Narrative comment builder
   --------------------------------------------------------------- */

function buildComment(r, race) {
  const parts = [];

  // 1. Career record opener
  if (r.career_runs != null && r.career_runs > 0) {
    parts.push(`${r.career_wins || 0} wins from ${r.career_runs} starts (${r.career_places || 0} placed).`);
  }

  // 2. Course and/or C&D form
  if (r.cd_wins > 0) {
    parts.push(`${r.cd_wins}-win course & distance record (${r.cd_runs} runs).`);
  } else if (r.course_wins > 0) {
    parts.push(`${r.course_wins} win${r.course_wins > 1 ? "s" : ""} at this course (${r.course_runs} runs).`);
  } else if (r.course_runs > 0 && r.course_wins === 0) {
    parts.push(`0 from ${r.course_runs} at this course.`);
  }

  // 3. Distance form (only if no C&D already mentioned)
  if (!r.cd_wins && r.dist_runs > 0) {
    if (r.dist_wins > 0) {
      parts.push(`${r.dist_wins} win${r.dist_wins > 1 ? "s" : ""} at this trip (${r.dist_runs} runs).`);
    } else if (r.dist_runs >= 3) {
      parts.push(`Yet to win at this distance (${r.dist_runs} attempts).`);
    }
  }

  // 4. Class change
  if (r.class_change === "up") parts.push("Steps up in class.");
  else if (r.class_change === "down") parts.push("Drops in class.");

  // 5. Distance change
  const dy = r.distance_change_yards;
  if (dy != null && Math.abs(dy) >= 220) {
    const furlongs = Math.abs(Math.round(dy / 220));
    if (dy > 0) parts.push(`Up ${furlongs}f in trip.`);
    else parts.push(`Back ${furlongs}f in trip.`);
  }

  // 6. First-time headgear
  if (r.first_time_headgear && r.headgear) {
    parts.push(`First-time ${r.headgear.toLowerCase()}.`);
  }

  // 7. Layoff
  if (r.dslr != null) {
    if (r.dslr > 180) {
      const months = Math.round(r.dslr / 30);
      parts.push(`Absent ${months} months.`);
    } else if (r.dslr > 60) {
      parts.push(`${r.dslr} days since last run.`);
    }
  }

  // 8. Going suitability
  if (r.bias_score != null && r.runs_on_going > 0) {
    if (r.bias_score >= 40) {
      parts.push(`Strong record on this ground (${r.runs_on_going} runs).`);
    } else if (r.bias_score >= 20) {
      parts.push(`Handles this ground (${r.runs_on_going} runs).`);
    } else if (r.bias_score <= -40) {
      parts.push(`Questionable on this going.`);
    }
  } else if (r.runs_on_going === 0) {
    parts.push("Unproven on today's going.");
  }

  // 9. Peak OR vs current (if dropping from a higher mark)
  if (r.peak_or && r.official_rating && r.peak_or > r.official_rating + 5) {
    parts.push(`Has been rated as high as ${r.peak_or}.`);
  }

  return parts.join(" ");
}

/* ---------------------------------------------------------------
   Star rating — composite score ranked within the field
   --------------------------------------------------------------- */

function computeStarRatings(runners) {
  // Score each runner 0–100 by averaging available dimensions
  const scores = runners.map((r) => {
    const dims = [];

    // Career strike rate
    if (r.career_runs > 0)
      dims.push(Math.min((r.career_wins / r.career_runs) * 100, 50) * 2);

    // Course win rate
    if (r.course_runs >= 2)
      dims.push(Math.min((r.course_wins / r.course_runs) * 100, 50) * 2);

    // Distance win rate
    if (r.dist_runs >= 2)
      dims.push(Math.min((r.dist_wins / r.dist_runs) * 100, 50) * 2);

    // C&D win rate
    if (r.cd_runs >= 1 && r.cd_wins > 0)
      dims.push(Math.min((r.cd_wins / r.cd_runs) * 100, 50) * 2);

    // Trainer SR
    if (r.trainer_sr != null && r.trainer_signal !== "insufficient")
      dims.push(Math.min(r.trainer_sr, 30) * (100 / 30));

    // Jockey SR
    if (r.jockey_sr != null && r.jockey_signal !== "insufficient")
      dims.push(Math.min(r.jockey_sr, 30) * (100 / 30));

    // Going bias (–100 to +100 → 0 to 100)
    if (r.bias_score != null)
      dims.push(Math.max(0, Math.min(100, (r.bias_score + 100) / 2)));

    return dims.length > 0 ? dims.reduce((a, b) => a + b, 0) / dims.length : 0;
  });

  // Rank: highest score = rank 0
  const sorted = [...scores].sort((a, b) => b - a);
  const n = runners.length;
  const starValues = scores.map((s) => {
    const rank = sorted.indexOf(s);
    // Map rank to 1–5 stars (top gets 5, bottom gets 1)
    if (n <= 1) return 3;
    const pct = 1 - rank / (n - 1); // 1.0 = best, 0.0 = worst
    if (pct >= 0.8) return 5;
    if (pct >= 0.6) return 4;
    if (pct >= 0.4) return 3;
    if (pct >= 0.2) return 2;
    return 1;
  });
  return starValues;
}

const STAR_FILLED = "\u2605";
const STAR_EMPTY  = "\u2606";

/* ---------------------------------------------------------------
   Sorting helpers
   --------------------------------------------------------------- */

const SORT_COLUMNS = {
  num:     { label: "#",       get: (r) => r.card_no ?? 0 },
  horse:   { label: "Horse",   get: (r) => r._horseSr ?? -1 },
  rating:  { label: "Rating",  get: (r) => r._stars ?? 0 },
  jockey:  { label: "Jockey",  get: (r) => r.jockey_sr ?? -1 },
  trainer: { label: "Trainer", get: (r) => r.trainer_sr ?? -1 },
  going:   { label: "Going",   get: (r) => r.bias_score ?? -999 },
  exch:    { label: "Exch",    get: (r) => r._exchPrice ?? 9999 },
};

function SortHeader({ colKey, sortCol, sortDir, onClick, className }) {
  const active = sortCol === colKey;
  const arrow = active ? (sortDir === "asc" ? " \u25B2" : " \u25BC") : "";
  return (
    <th
      className={`card-th card-th-sort ${className || ""} ${active ? "card-th-active" : ""}`}
      onClick={() => onClick(colKey)}
    >
      {SORT_COLUMNS[colKey].label}{arrow}
    </th>
  );
}

/* ---------------------------------------------------------------
   CardTable component
   --------------------------------------------------------------- */

function CardTable({ runners, race, prices = {}, priceHistory = {} }) {
  // NOTE: no early return before the hooks below — an empty-guard here would
  // violate the rules of hooks if `runners` toggled empty↔non-empty. The guard
  // is hoisted below all hooks, just before the JSX return.
  const hasPrices = Object.keys(prices).length > 0;
  // Snapshot history persists after the off (and survives cache-wiping deploys),
  // so show the price column from history even when no live price remains —
  // that's how the final pre-off price + sparkline stay on settled races.
  const hasHistory = Object.keys(priceHistory).length > 0;
  const showPriceCol = hasPrices || hasHistory;
  const colCount = showPriceCol ? 11 : 10;

  const [sortCol, setSortCol] = useState("num");
  const [sortDir, setSortDir] = useState("asc");
  const pricesSwitched = useRef(false);

  // Switch to Exch sort when prices first arrive
  useEffect(() => {
    if (hasPrices && !pricesSwitched.current) {
      pricesSwitched.current = true;
      setSortCol("exch");
      setSortDir("asc");
    }
  }, [hasPrices]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      // Default descending for everything except # (cloth number)
      setSortDir(col === "num" ? "asc" : "desc");
    }
  };

  // Enrich runners with computed values for sorting, then sort
  const enriched = useMemo(() => {
    if (!runners || runners.length === 0) return [];
    const starValues = computeStarRatings(runners);
    return runners.map((r, i) => ({
      ...r,
      _stars: starValues[i],
      _horseSr: r.career_runs > 0 ? Math.round((r.career_wins / r.career_runs) * 100) : null,
      // Live price if available, else the last recorded snapshot (final pre-off price)
      _exchPrice: prices[r.horse]
        ?? (r.horse_id != null && priceHistory[r.horse_id]?.length
          ? priceHistory[r.horse_id][priceHistory[r.horse_id].length - 1].price
          : null),
      _history: (r.horse_id != null && priceHistory[r.horse_id]) || null,
      _origIdx: i,
    }));
  }, [runners, prices, priceHistory, hasPrices]);

  const sorted = useMemo(() => {
    const getter = SORT_COLUMNS[sortCol]?.get || SORT_COLUMNS.num.get;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...enriched].sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [enriched, sortCol, sortDir]);

  // Empty-guard hoisted here (below all hooks) — see the note at the top.
  if (!runners || runners.length === 0) return null;

  return (
    <div className="card-table-wrap">
      <table className="card-table">
        <thead>
          <tr>
            <SortHeader colKey="num" sortCol={sortCol} sortDir={sortDir} onClick={handleSort} className="card-th-num" />
            <SortHeader colKey="horse" sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
            <th className="card-th card-th-meta">Age</th>
            <th className="card-th card-th-meta" title="Sex: F filly · C colt · G gelding · M mare · H horse · R rig">Sex</th>
            <th className="card-th card-th-meta">Wt</th>
            <th className="card-th card-th-meta">OR</th>
            <SortHeader colKey="rating" sortCol={sortCol} sortDir={sortDir} onClick={handleSort} className="card-th-rating" />
            <SortHeader colKey="jockey" sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
            <SortHeader colKey="trainer" sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
            <SortHeader colKey="going" sortCol={sortCol} sortDir={sortDir} onClick={handleSort} className="card-th-going" />
            {showPriceCol && <SortHeader colKey="exch" sortCol={sortCol} sortDir={sortDir} onClick={handleSort} className="card-th-price" />}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const horseSr = r._horseSr;
            const horseSignal = r.career_runs > 0 ? "ok" : null;
            const hc = srColour(horseSr, horseSignal);
            const jc = srColour(r.jockey_sr, r.jockey_signal);
            const tc = srColour(r.trainer_sr, r.trainer_signal);
            const gc = biasColour(r.bias_score);
            const exchPrice = r._exchPrice;
            const history = r._history;
            const starCount = r._stars;

            const comment = buildComment(r, race);

            return (
              <React.Fragment key={r.card_no}>
                <tr className="card-row">
                  {/* Cloth number */}
                  <td className="card-td card-td-num">
                    <span className="cloth-badge">{r.card_no}</span>
                  </td>

                  {/* Horse — coloured by career SR% */}
                  <td
                    className="card-td card-td-entity"
                    style={{ background: hc.bg, color: hc.text, textShadow: hc.shadow }}
                    title={
                      horseSr !== null
                        ? `SR ${horseSr}% (${r.career_wins}/${r.career_runs})`
                        : "No career form"
                    }
                  >
                    <img
                      className="card-horse-icon"
                      src={HORSE_ICONS[r._origIdx % HORSE_ICONS.length]}
                      alt=""
                    />
                    <span className="card-name">{r.horse}</span>
                    {horseSr !== null ? (
                      <span className="card-stat">
                        {horseSr}%
                        <span className="card-runs">({r.career_runs})</span>
                      </span>
                    ) : (
                      <span className="card-stat card-stat-na">—</span>
                    )}
                    {r.horse_id != null && (
                      <span className="card-track" style={{ marginLeft: "auto" }}>
                        <TrackButton
                          entityType="horse"
                          entityId={r.horse_id}
                          entityName={r.horse}
                        />
                      </span>
                    )}
                  </td>

                  {/* Age */}
                  <td className="card-td card-td-meta">
                    {r.age != null ? `${r.age}yo` : <span className="card-stat-na">—</span>}
                  </td>

                  {/* Sex */}
                  <td className="card-td card-td-meta">
                    {r.sex || <span className="card-stat-na">—</span>}
                  </td>

                  {/* Weight (lbs → stones-pounds) */}
                  <td className="card-td card-td-meta">
                    {formatWeight(r.weight_lbs) || <span className="card-stat-na">—</span>}
                  </td>

                  {/* Official Rating */}
                  <td className="card-td card-td-meta">
                    {r.official_rating != null ? r.official_rating : <span className="card-stat-na">—</span>}
                  </td>

                  {/* Star rating */}
                  <td className="card-td card-td-rating">
                    <span className="card-stars" title={`Rating: ${starCount}/5`}>
                      {STAR_FILLED.repeat(starCount)}{STAR_EMPTY.repeat(5 - starCount)}
                    </span>
                  </td>

                  {/* Jockey — coloured by Overall SR% */}
                  <td
                    className="card-td card-td-entity"
                    style={{ background: jc.bg, color: jc.text, textShadow: jc.shadow }}
                    title={
                      r.jockey_sr !== null
                        ? `SR ${r.jockey_sr}% (${r.jockey_runs} runs)`
                        : "Insufficient data"
                    }
                  >
                    <div className="card-name">{r.jockey}</div>
                    {r.jockey_sr !== null ? (
                      <span className="card-stat">
                        {r.jockey_sr}%
                        <span className="card-runs">({r.jockey_runs})</span>
                      </span>
                    ) : (
                      <span className="card-stat card-stat-na">—</span>
                    )}
                  </td>

                  {/* Trainer — coloured by Overall SR% */}
                  <td
                    className="card-td card-td-entity"
                    style={{ background: tc.bg, color: tc.text, textShadow: tc.shadow }}
                    title={
                      r.trainer_sr !== null
                        ? `SR ${r.trainer_sr}% (${r.trainer_runs} runs)`
                        : "Insufficient data"
                    }
                  >
                    <div className="card-name">{r.trainer}</div>
                    {r.trainer_sr !== null ? (
                      <span className="card-stat">
                        {r.trainer_sr}%
                        <span className="card-runs">({r.trainer_runs})</span>
                      </span>
                    ) : (
                      <span className="card-stat card-stat-na">—</span>
                    )}
                  </td>

                  {/* Going — coloured by bias score */}
                  <td
                    className="card-td card-td-going"
                    style={{ background: gc.bg, color: gc.text, textShadow: gc.shadow }}
                    title={`Going bias: ${r.bias_score ?? "N/A"}${r.runs_on_going > 0 ? ` (${r.runs_on_going} runs)` : ""}${r.used_adjacent ? " (adjacent)" : ""}`}
                  >
                    <span className="card-bias-score">
                      {r.bias_score !== null && r.bias_score !== undefined
                        ? (r.bias_score >= 0 ? "+" : "") + r.bias_score
                        : "—"}
                      {r.used_adjacent && <span className="card-adjacent">*</span>}
                    </span>
                  </td>

                  {/* Betfair Exchange price + intraday trend sparkline */}
                  {showPriceCol && (
                    <td className="card-td card-td-price">
                      {exchPrice != null ? (
                        <span className="card-price">{exchPrice.toFixed(1)}</span>
                      ) : (
                        <span className="card-stat card-stat-na">—</span>
                      )}
                      {history && history.length > 1 && (
                        <PriceSparkline history={history} width={62} height={24} />
                      )}
                    </td>
                  )}
                </tr>
                {comment && (
                  <tr className="card-comment-row">
                    <td colSpan={colCount} className="card-comment">
                      {comment}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default React.memo(CardTable);
